import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { ObjectTypeHoldMusic } from '@weave/schema-gen-ts/dist/apis/api/phone/audits/v1/api.pb';
import { PhoneConfigOrg } from '@wetags/phone-config';
import { useQuery } from 'react-query';
import HoldMusicAPI from '@frontend/api-hold-music';
import { scopedFilterAndSort } from '@frontend/filters';
import { useTranslation } from '@frontend/i18n';
import {
  SettingsTenantLocation,
  useHasPhoneSettingsAccess,
  usePhoneSettingsShallowStore,
} from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { TextLink, useModalControl } from '@frontend/design-system';
import { PhoneSettingsScopedLocationFilterMenu } from '../../components/common/index-table';
import { HoldMusicLocationList, ManageHoldMusicTray } from '../../components/hold-music/location-list';
import { LocationHoldMusic } from '../../components/hold-music/types';
import {
  DetailsHeader,
  LocationDetails,
} from '../../components/phone-system-history/history-details-tray/details-header';
import {
  HISTORY_TRAY_WIDTH,
  HistoryDetailsTray,
  useHistoryTrayShouldOverlap,
} from '../../components/phone-system-history/history-details-tray/history-details-tray';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';

export const HoldMusicSettings = () => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const { getScopeName } = useAppScopeStore();

  const { data } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listLocationHoldMusic()],
    queryFn: () => {
      return HoldMusicAPI.List({ tenantId: settingsTenantLocation?.phoneTenantId ?? '' });
    },
    select: (response) =>
      response?.locationHoldMusic?.sort((a, b) => getScopeName(a.locationId).localeCompare(getScopeName(b.locationId))),
    enabled: !!settingsTenantLocation?.phoneTenantId,
  });

  if (settingsTenantLocation && data) {
    return (
      <HoldMusicPageWithData
        availableLocations={globalAvailableLocationIds ?? []}
        tenantLocation={settingsTenantLocation}
        locationHoldMusic={data}
      />
    );
  }

  return (
    <PhoneSettingsPage
      title={t('Hold Music')}
      subtitle={t('Select the music that callers will hear when they are placed on hold.')}
      breadcrumbs={[
        { label: t('Phone'), to: '/phone/main' },
        { label: t('Hold Music'), to: '/phone/hold-music' },
      ]}
      renderContainer={false}
    >
      <div>{t('No Phone Tenant Selected')}</div>
    </PhoneSettingsPage>
  );
};

const HoldMusicPageWithData = ({
  availableLocations,
  tenantLocation,
  locationHoldMusic,
}: {
  availableLocations: string[];
  tenantLocation: SettingsTenantLocation;
  locationHoldMusic: LocationHoldMusic[];
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps } = useModalControl();
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const { getScopeName, accessibleLocationData } = useAppScopeStore();

  const { associatedLocationIds } = usePhoneSettingsShallowStore('associatedLocationIds');
  const selectedLocations = (associatedLocationIds ?? []).map((id) => accessibleLocationData[id]).filter(Boolean);
  const { hasPhoneSystemHistoryAccess } = useHasPhoneSettingsAccess({ selectedLocations });

  const [phoneSystemHistoryEnabled] = PhoneConfigOrg.useWetag('phone-system-history');

  // Keep track of which location's hold music history button is currently active (if any).
  const [activeHistoryLocationId, setActiveHistoryLocationId] = useState<string | undefined>();

  // Reset active history location when the tray is closed.
  const handleHistoryTrayClose = useCallback(() => setActiveHistoryLocationId(undefined), []);

  const historyTray = useModalControl({ onClose: handleHistoryTrayClose });
  const shouldOverlapHistoryTray = useHistoryTrayShouldOverlap();
  const isSquishedMode = historyTray.modalProps.show && !shouldOverlapHistoryTray;

  const isUnify = tenantLocation.locationType === 'unify';

  const resolvedLocations = filteredLocations.length > 0 ? filteredLocations : availableLocations;

  const filteredHoldMusic = useMemo(
    () =>
      scopedFilterAndSort({
        data: locationHoldMusic,
        scopeIds: availableLocations,
        filteredScopeIds: resolvedLocations,
        scopeIdAccessor: (holdMusic) => [holdMusic.locationId],
      }),
    [locationHoldMusic, availableLocations, resolvedLocations]
  );

  // Handler for per-card history button clicks
  const handleHistoryClick = (locationId: string) => {
    if (activeHistoryLocationId === locationId) {
      // Same button clicked - close tray
      historyTray.closeModal();
    } else {
      // Different location - open/switch tray
      if (!historyTray.modalProps.show) {
        historyTray.openModal();
      }
      setActiveHistoryLocationId(locationId);
    }
  };

  let trayData;

  if (resolvedLocations.length === 1) {
    trayData = {
      type: 'single' as const,
      label: getScopeName(resolvedLocations[0]),
      locationIds: resolvedLocations,
    };
  } else if (resolvedLocations.length !== availableLocations.length) {
    trayData = {
      type: 'multi' as const,
      label: t('{{count}} Locations', { count: resolvedLocations.length }),
      locationIds: resolvedLocations,
      mediaId: 'none',
    };
  } else {
    trayData = {
      type: 'multi' as const,
      label: tenantLocation.name ?? '',
      locationIds: resolvedLocations,
      mediaId: 'none',
    };
  }

  return (
    <>
      <PhoneSettingsPage
        title={t('Hold Music')}
        subtitle={t('Select the music that callers will hear when they are placed on hold.')}
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Hold Music'), to: '/phone/hold-music' },
        ]}
        renderContainer={false}
        css={css`
          width: ${isSquishedMode ? `calc(100% - ${HISTORY_TRAY_WIDTH}px)` : '100%'};
          transition: width 0.3s ease;
        `}
      >
        <div style={{ display: 'grid', gap: availableLocations.length > 1 ? theme.spacing(2) : undefined }}>
          {availableLocations.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                gap: theme.spacing(4),
                maxWidth: 860,
              }}
            >
              {availableLocations.length > 1 ? (
                <>
                  <PhoneSettingsScopedLocationFilterMenu
                    selectedOptions={filteredLocations}
                    onChange={setFilteredLocations}
                  />
                  <TextLink
                    size='large'
                    weight='bold'
                    css={{ width: 'fit-content', justifySelf: 'end' }}
                    {...triggerProps}
                  >
                    {resolvedLocations.length === availableLocations.length
                      ? t('Manage All Locations')
                      : t('Manage {{count}} Locations', { count: resolvedLocations.length })}
                  </TextLink>
                </>
              ) : null}
            </div>
          )}
          <HoldMusicLocationList
            tenantLocation={tenantLocation}
            locationHoldMusic={filteredHoldMusic}
            historyProps={
              phoneSystemHistoryEnabled && hasPhoneSystemHistoryAccess
                ? {
                    activeLocationId: activeHistoryLocationId,
                    onHistoryClick: handleHistoryClick,
                  }
                : undefined
            }
          />
        </div>
        <ManageHoldMusicTray tenantLocation={tenantLocation} modalProps={modalProps} data={trayData} />
      </PhoneSettingsPage>

      <HistoryDetailsTray
        modalControls={historyTray}
        tenantId={tenantLocation.phoneTenantId ?? ''}
        callObjectId={activeHistoryLocationId}
        callObjectType={ObjectTypeHoldMusic}
        HeaderComponent={
          <DetailsHeader objectType={ObjectTypeHoldMusic} onClose={historyTray.closeModal}>
            {activeHistoryLocationId && isUnify && (
              <LocationDetails locationNames={[getScopeName(activeHistoryLocationId)]} />
            )}
          </DetailsHeader>
        }
      />
    </>
  );
};
